/**
 * #480 Neural Network Flow 2026
 *
 * 清晰展示 AI 神经网络的数据处理流程：
 * 输入数据 → 逐层前向传播 → 输出结果
 *
 * 三阶段叙事：
 * 1. 输入阶段：数据（图像/文本/音频）进入网络
 * 2. 处理阶段：信号逐层激活，能量脉冲在连接间传播
 * 3. 输出阶段：结果节点亮起，展示 AI 的判断
 *
 * 适用场景：AI 产品介绍、科技品牌展示、机器学习教程
 */

import React from 'react';
import {
	AbsoluteFill,
	useCurrentFrame,
	useVideoConfig,
	interpolate,
	Easing,
} from 'remotion';

const PALETTE = {
	bg: '#070b1a',
	bgGradient: 'radial-gradient(ellipse at 50% 50%, #0f1535 0%, #070b1a 70%)',
	accent: '#00c6ff',
	accentGlow: 'rgba(0, 198, 255, 0.5)',
	pulse: '#7c3aed',
	pulseGlow: 'rgba(124, 58, 237, 0.6)',
	output: '#22d3ee',
	outputGlow: 'rgba(34, 211, 238, 0.5)',
	nodeIdle: '#1e2748',
	nodeBorder: 'rgba(0, 198, 255, 0.3)',
	connIdle: 'rgba(0, 198, 255, 0.08)',
	connActive: 'rgba(0, 198, 255, 0.35)',
	textPrimary: '#e2e8f0',
	textSecondary: 'rgba(255,255,255,0.45)',
};

interface LayerDef {
	count: number;
	label: string;
	sublabel: string;
	nodeLabels?: string[];
}

const LAYERS: LayerDef[] = [
	{
		count: 3,
		label: '输入层',
		sublabel: 'Input',
		nodeLabels: ['图像', '文本', '音频'],
	},
	{ count: 5, label: '特征提取', sublabel: 'Feature' },
	{ count: 6, label: '深度推理', sublabel: 'Reasoning' },
	{ count: 5, label: '模式匹配', sublabel: 'Pattern' },
	{
		count: 3,
		label: '输出层',
		sublabel: 'Output',
		nodeLabels: ['分类', '生成', '决策'],
	},
];

const NODE_R = 20;
const LAYER_ACTIVATION_DURATION = 18;

interface Pos {
	x: number;
	y: number;
}

function buildPositions(
	w: number,
	h: number,
): { layers: Pos[][]; cx: number[] } {
	const padX = w * 0.14;
	const cx: number[] = [];
	const gap = (w - padX * 2) / (LAYERS.length - 1);
	const layers: Pos[][] = LAYERS.map((def, li) => {
		const x = padX + li * gap;
		cx.push(x);
		const totalH = (def.count - 1) * 78;
		const startY = (h - totalH) / 2;
		return Array.from({ length: def.count }, (_, ni) => ({
			x,
			y: startY + ni * 78,
		}));
	});
	return { layers, cx };
}

function seededRand(a: number, b: number): number {
	const v = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
	return v - Math.floor(v);
}

interface Conn {
	from: Pos;
	to: Pos;
	weight: number;
	layerIdx: number;
}

function buildConnections(layers: Pos[][]): Conn[] {
	const result: Conn[] = [];
	for (let li = 0; li < layers.length - 1; li++) {
		for (let fi = 0; fi < layers[li].length; fi++) {
			for (let ti = 0; ti < layers[li + 1].length; ti++) {
				const w = seededRand(li * 100 + fi, ti * 37 + li);
				if (w > 0.25) {
					result.push({
						from: layers[li][fi],
						to: layers[li + 1][ti],
						weight: w,
						layerIdx: li,
					});
				}
			}
		}
	}
	return result;
}

function layerProgress(frame: number, layerIdx: number): number {
	const start = 15 + layerIdx * LAYER_ACTIVATION_DURATION;
	return Math.max(0, Math.min(1, (frame - start) / 20));
}

const ConnectionLine: React.FC<{
	conn: Conn;
	frame: number;
}> = ({ conn, frame }) => {
	const { from, to, weight, layerIdx } = conn;
	const p = layerProgress(frame, layerIdx);

	const baseOpacity = weight * 0.06 + 0.02;
	const activeOpacity = baseOpacity + p * weight * 0.28;

	const midX = (from.x + to.x) / 2;
	const cpOffset = (to.y - from.y) * 0.2;
	const path = `M${from.x},${from.y} C${midX - cpOffset},${from.y} ${midX + cpOffset},${to.y} ${to.x},${to.y}`;

	const particleT =
		p > 0
			? ((frame * 0.025 + weight * 5 + layerIdx * 0.7) % 1.0)
			: -1;

	const t = particleT;
	const mt = 1 - t;
	const px =
		t >= 0
			? mt * mt * mt * from.x +
				3 * mt * mt * t * (midX - cpOffset) +
				3 * mt * t * t * (midX + cpOffset) +
				t * t * t * to.x
			: 0;
	const py =
		t >= 0
			? mt * mt * mt * from.y +
				3 * mt * mt * t * from.y +
				3 * mt * t * t * to.y +
				t * t * t * to.y
			: 0;

	return (
		<g>
			<path
				d={path}
				fill="none"
				stroke={p > 0 ? PALETTE.connActive : PALETTE.connIdle}
				strokeWidth={weight * 1.5 + 0.5}
				opacity={activeOpacity}
			/>
			{t >= 0 && (
				<circle
					cx={px}
					cy={py}
					r={3}
					fill={PALETTE.pulse}
					opacity={0.9}
					style={{
						filter: `drop-shadow(0 0 5px ${PALETTE.pulseGlow})`,
					}}
				/>
			)}
		</g>
	);
};

const NetworkNode: React.FC<{
	pos: Pos;
	layerIdx: number;
	nodeIdx: number;
	label?: string;
	frame: number;
	isInput: boolean;
	isOutput: boolean;
}> = ({ pos, layerIdx, nodeIdx, label, frame, isInput, isOutput }) => {
	const p = layerProgress(frame, layerIdx);
	const breathe = Math.sin(frame * 0.08 + nodeIdx * 1.2) * 0.12 + 1;
	const r = NODE_R * (p > 0 ? breathe : 0.85);

	const nodeColor = isOutput
		? PALETTE.output
		: p > 0
			? PALETTE.accent
			: PALETTE.nodeIdle;
	const glowColor = isOutput ? PALETTE.outputGlow : PALETTE.accentGlow;

	const outerRingR = r * 2.2;
	const outerRingOpacity = p * 0.18;
	const innerHighlight = p * 0.7;

	const labelOpacity = interpolate(p, [0, 0.6], [0, 1], {
		extrapolateRight: 'clamp',
	});

	return (
		<g>
			{p > 0 && (
				<circle
					cx={pos.x}
					cy={pos.y}
					r={outerRingR}
					fill="none"
					stroke={glowColor}
					strokeWidth={1.5}
					opacity={outerRingOpacity}
				/>
			)}
			{p > 0 && (
				<circle
					cx={pos.x}
					cy={pos.y}
					r={r * 1.6}
					fill={glowColor}
					opacity={p * 0.12}
				/>
			)}
			<circle
				cx={pos.x}
				cy={pos.y}
				r={r}
				fill={nodeColor}
				stroke={p > 0 ? nodeColor : PALETTE.nodeBorder}
				strokeWidth={p > 0 ? 2 : 1}
				opacity={p > 0 ? 0.95 : 0.5}
				style={
					p > 0
						? { filter: `drop-shadow(0 0 10px ${glowColor})` }
						: undefined
				}
			/>
			{p > 0 && (
				<circle
					cx={pos.x}
					cy={pos.y}
					r={r * 0.35}
					fill="#fff"
					opacity={innerHighlight}
				/>
			)}
			{label && (
				<text
					x={pos.x}
					y={
						isInput
							? pos.y - NODE_R * 2 + 9
							: isOutput
								? pos.y - NODE_R * 2 + 9
								: pos.y + NODE_R * 2 + 27
					}
					textAnchor="middle"
					fill={PALETTE.textPrimary}
					fontSize={22}
					fontFamily="'PingFang SC','Noto Sans SC','Microsoft YaHei',sans-serif"
					fontWeight={600}
					opacity={labelOpacity}
				>
					{label}
				</text>
			)}
		</g>
	);
};

const ProgressBar: React.FC<{ frame: number; total: number }> = ({
	frame,
	total,
}) => {
	const barW = 320;
	const progress = Math.min(1, frame / (total * 0.85));
	const fillW = barW * progress;
	const barOpacity = interpolate(frame, [10, 30], [0, 0.8], {
		extrapolateRight: 'clamp',
	});

	const pct = Math.round(progress * 100);

	return (
		<div
			style={{
				position: 'absolute',
				bottom: 34,
				left: '50%',
				transform: 'translateX(-50%)',
				display: 'flex',
				alignItems: 'center',
				gap: 14,
				opacity: barOpacity,
			}}
		>
			<span
				style={{
					fontFamily:
						"'SF Mono','Fira Code','Consolas',monospace",
					fontSize: 24,
					color: PALETTE.textSecondary,
					letterSpacing: 1,
				}}
			>
				PROCESSING
			</span>
			<div
				style={{
					width: barW,
					height: 4,
					borderRadius: 2,
					background: 'rgba(255,255,255,0.08)',
					overflow: 'hidden',
				}}
			>
				<div
					style={{
						width: fillW,
						height: '100%',
						borderRadius: 2,
						background: `linear-gradient(90deg, ${PALETTE.accent}, ${PALETTE.pulse})`,
						boxShadow: `0 0 12px ${PALETTE.accentGlow}`,
					}}
				/>
			</div>
			<span
				style={{
					fontFamily:
						"'SF Mono','Fira Code','Consolas',monospace",
					fontSize: 24,
					color: PALETTE.accent,
					minWidth: 40,
				}}
			>
				{pct}%
			</span>
		</div>
	);
};

export const NeuralNetworkFlow2026: React.FC = () => {
	const frame = useCurrentFrame();
	const { width, height, durationInFrames } = useVideoConfig();

	const { layers: positions, cx } = React.useMemo(
		() => buildPositions(width, height),
		[width, height],
	);
	const connections = React.useMemo(
		() => buildConnections(positions),
		[positions],
	);

	const titleOpacity = interpolate(frame, [0, 25], [0, 1], {
		extrapolateRight: 'clamp',
		easing: Easing.out(Easing.cubic),
	});
	const titleY = interpolate(frame, [0, 25], [16, 0], {
		extrapolateRight: 'clamp',
		easing: Easing.out(Easing.cubic),
	});

	const allDone = frame > 15 + (LAYERS.length - 1) * LAYER_ACTIVATION_DURATION + 20;
	const resultOpacity = allDone
		? interpolate(
				frame,
				[
					15 + (LAYERS.length - 1) * LAYER_ACTIVATION_DURATION + 20,
					15 + (LAYERS.length - 1) * LAYER_ACTIVATION_DURATION + 40,
				],
				[0, 1],
				{ extrapolateRight: 'clamp' },
			)
		: 0;

	return (
		<AbsoluteFill
			style={{
				background: PALETTE.bgGradient,
				overflow: 'hidden',
			}}
		>
			{/* 标题 */}
			<div
				style={{
					position: 'absolute',
					top: 38,
					left: 0,
					right: 0,
					textAlign: 'center',
					opacity: titleOpacity,
					transform: `translateY(${titleY}px)`,
				}}
			>
				<div
					style={{
						fontFamily:
							"'PingFang SC','Noto Sans SC','Microsoft YaHei',sans-serif",
						fontSize: 50,
						fontWeight: 700,
						color: PALETTE.textPrimary,
						letterSpacing: 4,
					}}
				>
					神经网络前向传播
				</div>
				<div
					style={{
						fontFamily:
							"'SF Mono','Fira Code','Consolas',monospace",
						fontSize: 26,
						color: PALETTE.textSecondary,
						marginTop: 6,
						letterSpacing: 2,
					}}
				>
					NEURAL NETWORK FORWARD PROPAGATION
				</div>
			</div>

			{/* 网络主体 */}
			<svg
				width={width}
				height={height}
				style={{ position: 'absolute', top: 0, left: -100 }}
			>
				<defs>
					<radialGradient id="bgGlow" cx="50%" cy="50%" r="50%">
						<stop offset="0%" stopColor={PALETTE.accent} stopOpacity={0.03} />
						<stop offset="100%" stopColor={PALETTE.bg} stopOpacity={0} />
					</radialGradient>
				</defs>
				<rect width={width} height={height} fill="url(#bgGlow)" />

				{connections.map((c, i) => (
					<ConnectionLine key={i} conn={c} frame={frame} />
				))}

				{positions.map((layer, li) =>
					layer.map((pos, ni) => (
						<NetworkNode
							key={`${li}-${ni}`}
							pos={pos}
							layerIdx={li}
							nodeIdx={ni}
							label={LAYERS[li].nodeLabels?.[ni]}
							frame={frame}
							isInput={li === 0}
							isOutput={li === LAYERS.length - 1}
						/>
					)),
				)}
			</svg>

			{/* 层标签 */}
			{LAYERS.map((def, li) => {
				const p = layerProgress(frame, li);
				const opacity = interpolate(p, [0, 0.5], [0.2, 0.85], {
					extrapolateRight: 'clamp',
				});
				return (
					<div
						key={li}
						style={{
							position: 'absolute',
							bottom: 120,
							left: cx[li] - 60,
							width: 120,
							textAlign: 'center',
							opacity,
						}}
					>
						<div
							style={{
								fontFamily:
									"'PingFang SC','Noto Sans SC','Microsoft YaHei',sans-serif",
								fontSize: 26,
								fontWeight: 600,
								color: PALETTE.textPrimary,
							}}
						>
							{def.label}
						</div>
						<div
							style={{
								fontFamily:
									"'SF Mono','Fira Code','Consolas',monospace",
								fontSize: 24,
								color: PALETTE.textSecondary,
								marginTop: 2,
							}}
						>
							{def.sublabel}
						</div>
					</div>
				);
			})}

			{/* 结果提示 */}
			{resultOpacity > 0 && (
				<div
					style={{
						position: 'absolute',
						top: height / 2 - 50,
						right: -10,
						width: 350,
						textAlign: 'center',
						opacity: resultOpacity,
					}}
				>
					<div
						style={{
							fontFamily:
								"'SF Mono','Fira Code','Consolas',monospace",
							fontSize: 22,
							color: PALETTE.output,
							letterSpacing: 1,
							marginBottom: 8,
						}}
					>
						✓ INFERENCE COMPLETE
					</div>
					<div
						style={{
							fontFamily:
								"'PingFang SC','Noto Sans SC',sans-serif",
							fontSize: 20,
							color: PALETTE.textSecondary,
							lineHeight: 1.6,
						}}
					>
						输入数据经过 {LAYERS.length - 2} 层深度处理
						<br />
						输出置信度 98.7%
					</div>
				</div>
			)}

			<ProgressBar frame={frame} total={durationInFrames} />
		</AbsoluteFill>
	);
};
